import { debounce } from '../../../scripts/utils/debounce';
import fillProduct from './fillProduct';
import fillPredictions from './fillPredictions';
import { getQuery, sendAPIRequest, getUser } from './utils';

const RELEWISE_DATASET_ID = relewiseData.relewise_dataset_id;
const RELEWISE_API_KEY = 'Bearer ' + relewiseData.relewise_api_key;
const RELEWISE_SERVER_URL = relewiseData.relewise_server_url;
const RELEWISE_SEARCH_ACTIVE = relewiseData.use_relewise_search;
let use_relewise = false;
if (RELEWISE_API_KEY && RELEWISE_DATASET_ID && RELEWISE_SERVER_URL && RELEWISE_SEARCH_ACTIVE) use_relewise = true;

if (use_relewise) {
  class PredictiveSearch extends HTMLElement {
    constructor() {
      super();
      setTimeout(() => {
        this.cachedResults = {};
        this.input = this.querySelector('input[type="search"]');
        this.language = relewiseData.current_locale;
        this.currency = relewiseData.current_currency;
        this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
        this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
        this.isOpen = false;
        this.abortController = new AbortController();
        this.searchTerm = '';
        this.template = relewiseData.current_template ? relewiseData.current_template : 'search';
        this.backdropClasses = 'bg-white backdrop-blur-sm bg-opacity-50 fixed inset-0 z-5';

        this.setupEventListeners();
      }, 500);
    }

    async getPopularData() {
      if (relewiseData.popular_products) {
        this.popularProducts = relewiseData.popular_products;
      } else {
        this.popularProducts = await this.getPopularProducts();
        relewiseData.popular_products = this.popularProducts;
      }
      if (relewiseData.popular_search_terms) {
        this.popularSearchTerms = relewiseData.popular_search_terms;
      } else {
        this.popularSearchTerms = await this.getPopularSearchTerms();
        relewiseData.popular_search_terms = this.popularSearchTerms;
      }
      return
    }

    createBackdrop() {
      const backdropEl = document.createElement('div');
      backdropEl.setAttribute('collapse-backdrop', '');
      backdropEl.classList.add(...this.backdropClasses.split(' '));
      if (!document.body.querySelector(`[collapse-backdrop]`)) {
        document.body.append(backdropEl);
      }
    }

    destroyBackdropEl() {
      const backdropEl = document.querySelector(`[collapse-backdrop]`);
      if (backdropEl) {
        backdropEl.remove();
      }
    }

    setupEventListeners() {
      this.addClearButton();
      let typingTimer;
      const typingDelay = 3000;

      this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));
      this.input.form.addEventListener('reset', this.onFormReset.bind(this));

      this.input.addEventListener('input', () => {
        clearTimeout(typingTimer);
        typingTimer = setTimeout(() => {
          this.onFocusIn();
        }, typingDelay);
      });

      this.addEventListener('focusout', (event) => {
        clearTimeout(typingTimer);
        this.onFocusOut(event);
      });

      this.addEventListener('keyup', (event) => {
        clearTimeout(typingTimer);
        typingTimer = setTimeout(() => {
          this.onKeyup(event);
        }, typingDelay);
      });

      this.addEventListener('keydown', (event) => {
        clearTimeout(typingTimer);
        this.onKeydown(event);
      });

      document.querySelector('[data-open-drawer="predictive-search"]')?.addEventListener('click', (event) => {
        const value = this.input.value;
        this.input.focus();
        if (value) {
          this.input.setSelectionRange(value.length, value.length);
          this.onChange();
        }
      });

      // Close the predictive search when the user scrolls down
      window.addEventListener('scroll', () => {
        if (window.innerWidth > 1024) {
          const predictiveSearchElement = this.querySelector('.thb-predictive-search--inner');
          if (predictiveSearchElement) {
            const observer = new IntersectionObserver(
              (entries) => {
                entries.forEach((entry) => {
                  if (!entry.isIntersecting) {
                    this.close();
                    observer.disconnect(); // Stop observing once closed
                  }
                });
              },
              { root: null, threshold: 0 }
            );
            observer.observe(predictiveSearchElement);
          }
        }
      });

      this.input.addEventListener(
        'input',
        debounce((event) => {
          this.onChange(event);
        }, 300).bind(this)
      );
    }

    async onChange() {
      this.open();
      this.setFormAction('/search');
      const newSearchTerm = getQuery(this.input.value);
      if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
        // Remove the results when they are no longer relevant for the new search term
        // so they don't show up when the dropdown opens again
        this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
      }
      // Update the term asap, don't wait for the predictive search query to finish loading
      this.updateSearchForTerm(this.searchTerm, newSearchTerm);

      this.searchTerm = newSearchTerm;
      if (this.searchTerm.length === 13 && this.searchTerm.charAt(0) === '0') {
        this.searchTerm = this.searchTerm.substring(1);
        console.log('first 0 cut due to special barcode search rule, now searching for:', this.searchTerm);
      }

      if (!this.searchTerm.length) {
        this.close(true);
        this.clearButtonhide();
        return;
      } else {
        this.clearButtonshow();
      }

      const results = await this.getBatchedPredictiveSearchResults();

      if (results.responses && results.responses.length > 0) {
        this.searchResultDataPredictions = results.responses[0];
        this.searchResultDataProducts = results.responses[1];
        this.searchResultDataCollections = results.responses[2];

        this.renderSearchResults();
      }

      if (results.responses[1] != null && results.responses[1].redirects != null && results.responses[1].redirects.length > 0 && results.responses[1].redirects[0].destination) {
        this.setFormAction(results.responses[1].redirects[0].destination);
      }
    }

    setFormAction(url) {
      this.input.form.action = url;
    }

    async getBatchedPredictiveSearchResults() {
      const relewiseSearchAPIEndpoint = RELEWISE_SERVER_URL + RELEWISE_DATASET_ID + '/v1/searchRequestCollection';
      const user = await getUser();
      const body = {
        "$type": "Relewise.Client.Requests.Search.SearchRequestCollection, Relewise.Client",
        "Requests": [
          {
            "$type": "Relewise.Client.Requests.Search.SearchTermPredictionRequest, Relewise.Client",
            "Term": this.searchTerm,
            "Take": 5,
            "Settings": {
              "TargetEntityTypes": [
                "Product",
                "Variant",
                "ProductCategory",
                "Brand"
              ]
            },
            "Language": {
              "Value": "da"
            },
            "Currency": {
              "Value": this.currency
            },
            "User": user,
          },
          {
            "$type": "Relewise.Client.Requests.Search.ProductSearchRequest, Relewise.Client",
            "Term": this.searchTerm,
            "Settings": {
              "SelectedProductProperties": {
                "DisplayName": true,
                "CategoryPaths": true,
                "Assortments": false,
                "Pricing": true,
                "AllData": true,
                "ViewedByUserInfo": false,
                "PurchasedByUserInfo": false,
                "Brand": true,
                "AllVariants": true,
                "ViewedByUserCompanyInfo": false,
                "PurchasedByUserCompanyInfo": false
              },
              "SelectedVariantProperties": {
                "DisplayName": true,
                "Pricing": false,
                "AllSpecifications": false,
                "Assortments": false,
                "AllData": true
              },
              "Recommendations": {}
            },
            "Skip": 0,
            "Take": 8,
            "Language": {
              "Value": "da"
            },
            "Currency": {
              "Value": this.currency
            },
            "User": user,
            "DisplayedAtLocation": this.template,
            "Filters": {
              "Items": [
                {
                  "$type": "Relewise.Client.Requests.Filters.VariantDataFilter, Relewise.Client",
                  "Key": "intersport-dk.myshopify.com_SellableOnlineQuantity",
                  "FilterOutIfKeyIsNotFound": true,
                  "MustMatchAllConditions": true,
                  "Conditions": {
                    "Items": [
                      {
                        "$type": "Relewise.Client.Requests.Conditions.GreaterThanCondition, Relewise.Client",
                        "Value": 0.0,
                        "TypeName": "GreaterThanCondition",
                        "Negated": false
                      }
                    ]
                  },
                  "Language": {
                    "Value": "da"
                  },
                  "Currency": {
                    "Value": this.currency
                  },
                  "TypeName": "VariantDataFilter",
                  "Negated": false
                }
              ]
            }
          },
          {
            "$type": "Relewise.Client.Requests.Search.ProductCategorySearchRequest, Relewise.Client",
            "Term": this.searchTerm,
            "Settings": {
              "SelectedCategoryProperties": {
                "DisplayName": true,
                "Paths": true,
                "Assortments": false,
                "ViewedByUserInfo": false,
                "AllData": true
              },
              "Recommendations": {}
            },
            "Skip": 0,
            "Take": 5,
            "Language": {
              "Value": "da"
            },
            "Currency": {
              "Value": this.currency
            },
            "User": user,
            "DisplayedAtLocation": this.template
          }
        ],
        "Language": {
          "Value": "da"
        },
        "Currency": {
          "Value": this.currency
        },
        "User": {
          "TemporaryId": "hDy1MAmli0B7VWlgNYPwJXP58lZVvaFEGb6t7C41bwr1LdCf3Si2dw==",
          "Classifications": {
            "Country": "DK"
          },
          "Identifiers": {},
          "Data": {}
        },
        "DisplayedAtLocation": this.template,
        "RelevanceModifiers": {},
        "Filters": {},
        "PostFilters": {}
      }
      return await sendAPIRequest(relewiseSearchAPIEndpoint, body, RELEWISE_API_KEY);
    }

    async getPopularSearchTerms() {
      const relewiseSearchAPIEndpoint = RELEWISE_SERVER_URL + RELEWISE_DATASET_ID + '/v1/PopularSearchTermsRecommendationRequest';
      const body = {
        "$type": "Relewise.Client.Requests.Recommendations.PopularSearchTermsRecommendationRequest, Relewise.Client",
        "displayedAtLocation": this.template,
        "Settings": {
          "NumberOfRecommendations": 5
        },
        "language": {
          "value": 'da'
        },
        "currency": {
          "value": this.currency
        },
        "user": await getUser()
      }
      return await sendAPIRequest(relewiseSearchAPIEndpoint, body, RELEWISE_API_KEY);
    }

    async getPopularProducts() {
      const relewiseSearchAPIEndpoint = RELEWISE_SERVER_URL + RELEWISE_DATASET_ID + '/v1/PopularProductsRequest';
      const body = {
        "$type": "Relewise.Client.Requests.Recommendations.PopularProductsRequest, Relewise.Client",
        "BasedOn": "MostPurchased",
        "displayedAtLocation": this.template,
        "SinceMinutesAgo": 20160,
        "Settings": {
          "NumberOfRecommendations": 8,
          "AllowFillIfNecessaryToReachNumberOfRecommendations": true,
          "AllowReplacingOfRecentlyShownRecommendations": true,
          "RecommendVariant": true,
          "ExplodedVariants": 1,
          "SelectedProductProperties": {
            "$type": "Relewise.Client.Requests.Shared.SelectedProductPropertiesSettings, Relewise.Client",
            "DisplayName": true,
            "CategoryPaths": true,
            "Assortments": true,
            "Pricing": true,
            "AllData": true,
            "AllVariants": true,
          },
          "selectedVariantProperties": {
            "displayName": true,
            "allData": true,
          },
          "PrioritizeDiversityBetweenRequests": false,
        },
        "language": {
          "value": 'da'
        },
        "currency": {
          "value": this.currency
        },
        "user": await getUser(),
        "Filters": {
          "$type": "Relewise.Client.Requests.Filters.FilterCollection, Relewise.Client",
          "Items": [
            {
              "$type": "Relewise.Client.Requests.Filters.VariantDataFilter, Relewise.Client",
              "Key": "intersport-dk.myshopify.com_SellableOnlineQuantity",
              "FilterOutIfKeyIsNotFound": true,
              "MustMatchAllConditions": true,
              "Conditions": {
                "Items": [
                  {
                    "$type": "Relewise.Client.Requests.Conditions.GreaterThanCondition, Relewise.Client",
                    "Value": 0.0,
                    "TypeName": "GreaterThanCondition",
                    "Negated": false
                  }
                ]
              },
              "Language": {
                "Value": "da"
              },
              "Currency": {
                "Value": this.currency
              },
              "TypeName": "VariantDataFilter",
              "Negated": false
            }
          ]
        }
      }
      return await sendAPIRequest(relewiseSearchAPIEndpoint, body, RELEWISE_API_KEY);
    }

    onFormSubmit(event) {
      if (!getQuery(this.input.value).length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
    }

    shouldResetForm() {
      return !document.querySelector('[aria-selected="true"] a');
    }

    onFormReset(event) {
      // Prevent default so the form reset doesn't set the value gotten from the url on page load
      event.preventDefault();
      // Don't reset if the user has selected an element on the predictive search dropdown
      if (this.shouldResetForm()) {
        this.input.value = '';
        this.input.focus();
        this.clearButtonhide();
      }
    }

    onFocusIn(event) {
      if (event.target === this.input) {
        const currentSearchTerm = getQuery(this.input.value);

        if (!currentSearchTerm.length) {
          this.clearButtonhide();
          return;
        } else {
          this.clearButtonshow();
        }
      }
    }

    onFocusOut(e) {
      setTimeout(() => {
        if (e.relatedTarget !== this.clearButton) {
          this.clearButtonhide();
        }
        if (!this.contains(document.activeElement)) this.close();
      });
    }

    onKeyup(event) {
      if (!getQuery(this.input.value).length) this.close(true);
      event.preventDefault();

      switch (event.code) {
        case 'ArrowUp':
          this.switchOption('up');
          break;
        case 'ArrowDown':
          this.switchOption('down');
          break;
        case 'Enter':
          this.selectOption();
          break;
      }
    }

    onKeydown(event) {
      // Prevent the cursor from moving in the input when using the up and down arrow keys
      if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
        event.preventDefault();
      }
    }

    updateSearchForTerm(previousTerm, newTerm) {
      const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
      const currentButtonText = searchForTextElement?.innerText;
      if (currentButtonText && currentButtonText.match(new RegExp(previousTerm, 'g'))) {
        if (currentButtonText.match(new RegExp(previousTerm, 'g')).length > 1) {
          // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
          return;
        }
        const newButtonText = currentButtonText.replace(previousTerm, newTerm);
        searchForTextElement.innerText = newButtonText;
      }
    }

    switchOption(direction) {
      if (!this.getAttribute('open')) return;
      const moveUp = direction === 'up';
      const selectedElement = this.querySelector('[aria-selected="true"]');

      const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter((element) => element.offsetParent !== null);
      let activeElementIndex = 0;

      if (moveUp && !selectedElement) return;

      let selectedElementIndex = -1;
      let i = 0;

      while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
        if (allVisibleElements[i] === selectedElement) {
          selectedElementIndex = i;
        }
        i++;
      }

      if (!moveUp && selectedElement) {
        activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
      } else if (moveUp) {
        activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
      }

      if (activeElementIndex === selectedElementIndex) return;

      const activeElement = allVisibleElements[activeElementIndex];

      activeElement.setAttribute('aria-selected', true);
      if (selectedElement) selectedElement.setAttribute('aria-selected', false);

      this.input.setAttribute('aria-activedescendant', activeElement.id);
    }

    selectOption() {
      const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

      if (selectedOption) selectedOption.click();
    }

    setLiveRegionLoadingState() {
      this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

      this.setAttribute('loading', true);
    }

    fillProducts(products, predictiveSearchWrapper) {
      const searchResultGrid = predictiveSearchWrapper.querySelector('#product-grid-search-drawer');
      products.forEach(product => {
        // Build product card from template
        const productCard = fillProduct(product);
        searchResultGrid.appendChild(productCard);
      });
      return predictiveSearchWrapper;
    }

    async renderSearchResults() {
      if (this.predictiveSearchResults.querySelector('.thb-predictive-search--inner')) this.predictiveSearchResults.querySelector('.thb-predictive-search--inner').remove();
      const wrapperTemplate = this.querySelector('#precidictive-search-relewise-template')?.content.cloneNode(true);
      const faqTemplate = this.querySelector('#precidictive-search-relewise-faq-template')?.content.cloneNode(true);
      if (!wrapperTemplate) return;
      if (this.searchResultDataPredictions.predictions.length > 0) fillPredictions(this.searchResultDataPredictions.predictions, wrapperTemplate, 'terms', this.input.value);
      if (this.searchResultDataCollections.results.length > 0) fillPredictions(this.searchResultDataCollections.results, wrapperTemplate, 'collections', this.input.value);

      if (this.searchResultDataProducts.results.length > 0) {
        const suggestionHeader = wrapperTemplate.querySelector('.thb-predictive-search--inner') ? wrapperTemplate.querySelector('.thb-predictive-search--inner').getAttribute('data-ts-products') : 'Top Suggestions';
        wrapperTemplate.querySelector('.thb-predictive-search--content .thb-predictive-search--heading').textContent = suggestionHeader;

        this.fillProducts(this.searchResultDataProducts.results, wrapperTemplate);
      } else {
        const suggestionHeader = wrapperTemplate.querySelector('.thb-predictive-search--inner') ? wrapperTemplate.querySelector('.thb-predictive-search--inner').getAttribute('data-ts-no-results') : "No suggestions found, why not try one of the popular products below?";
        wrapperTemplate.querySelector('.thb-predictive-search--content .thb-predictive-search--heading').textContent = suggestionHeader;
        wrapperTemplate.querySelector('.button--primary').classList.add('hidden');
        await this.getPopularData();
        if (this.popularProducts.recommendations.length > 0) this.fillProducts(this.popularProducts.recommendations, wrapperTemplate);
        if (this.popularSearchTerms.recommendations.length > 0) {
          fillPredictions(this.popularSearchTerms.recommendations, wrapperTemplate, 'popular-terms', this.input.value);
        }
      }
      if (faqTemplate) {
        if (window.innerWidth > 768) {
          wrapperTemplate.querySelector('.thb-predictive-search--sidebar').appendChild(faqTemplate);
        } else {
          wrapperTemplate.querySelector('.thb-predictive-search--content #product-grid-search-drawer').after(faqTemplate);
          wrapperTemplate.querySelector('.thb-predictive-search--content .sidebar-category-wrapper').classList.add('mt-4')
        }
      }
      this.predictiveSearchResults.appendChild(wrapperTemplate);
      this.setAttribute('results', true);

      this.setLiveRegionResults();
    }

    setLiveRegionResults() {
      this.removeAttribute('loading');
    }

    getResultsMaxHeight() {
      const headerSearchDrawer = document.querySelector('#Header-search-drawer');
      if (headerSearchDrawer) {
        this.resultsMaxHeight = window.innerHeight - headerSearchDrawer.getBoundingClientRect().bottom;
        return this.resultsMaxHeight;
      }
    }

    open() {
      this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
      this.setAttribute('open', true);

      this.input.setAttribute('aria-expanded', true);
      this.isOpen = true;
      this.createBackdrop();
    }

    close(clearSearchTerm = false) {
      if (window.innerWidth > 1023) {
        this.predictiveSearchResults.querySelector('.thb-predictive-search--inner')?.remove();
        this.closeResults(clearSearchTerm);
        this.isOpen = false;
      }

      this.destroyBackdropEl();
    }

    closeResults(clearSearchTerm = false) {
      if (clearSearchTerm) {
        this.input.value = '';
        this.removeAttribute('results');
      }
      const selected = this.querySelector('[aria-selected="true"]');

      if (selected) selected.setAttribute('aria-selected', false);

      this.input.setAttribute('aria-activedescendant', '');
      this.removeAttribute('loading');
      this.removeAttribute('open');
      this.input.setAttribute('aria-expanded', false);
      this.resultsMaxHeight = false;
      this.predictiveSearchResults.removeAttribute('style');
    }

    addClearButton() {
      this.clearButton = document.createElement('button');
      this.clearButton.id = 'custom-search-clear';
      this.clearButton.classList.add('hidden');
      this.clearButton.setAttribute('type', 'reset');
      if (this.input) this.input.insertAdjacentElement('afterend', this.clearButton);
    }

    clearButtonshow() {
      if (this.clearButton) this.clearButton.classList.remove('hidden');
      this.input.classList.add('!pr-8');
    }
    clearButtonhide() {
      if (this.clearButton) this.clearButton.classList.add('hidden');
      this.input.classList.remove('!pr-8');
    }
  }

  customElements.define('predictive-search', PredictiveSearch);
}